import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import { positionApi } from '@/entities/Position';
import type { Position, PositionFormData } from '@/entities/Position';

export const usePositionStore = defineStore('position-management', () => {
  const positions = ref<Position[]>([]);
  const currentPosition = ref<Position | null>(null);
  const isLoading = ref(false);
  const isCreating = ref(false);
  const isUpdating = ref(false);
  const isDeleting = ref(false);
  const error = ref<string | null>(null);

  //getters
  const positionCount = computed(() => positions.value.length);
  const hasPositions = computed(() => positions.value.length > 0);
  const activePositions = computed(() => positions.value.filter((p) => p.active));

  async function runWithFlag<T>(
    flag: { value: boolean },
    fallbackMessage: string,
    action: () => Promise<T>
  ): Promise<{ success: boolean; result?: T }> {
    try {
      flag.value = true;
      error.value = null;
      const result = await action();
      return { success: true, result };
    } catch (err: any) {
      error.value = err.message || fallbackMessage;
      return { success: false };
    } finally {
      flag.value = false;
    }
  }

  //actions
  // Note: For search/pagination on /admin/cargos, use useEntitySearch composable
  // This method is kept for backward compatibility with other features
  async function fetchAllPositions(): Promise<boolean> {
    const { success, result } = await runWithFlag(isLoading, 'Failed to fetch positions', () =>
      positionApi.getAllPositions()
    );
    if (success && result) {
      positions.value = result.data || [];
    }
    return success;
  }

  async function fetchPositionById(positionId: number | string): Promise<Position | null> {
    const { success, result } = await runWithFlag(isLoading, 'Failed to fetch position', () =>
      positionApi.getPositionById(positionId)
    );
    if (success && result) {
      currentPosition.value = result;
      return result;
    }
    return null;
  }

  async function createPosition(formData: PositionFormData): Promise<boolean> {
    const { success, result } = await runWithFlag(isCreating, 'Failed to create position', () =>
      positionApi.createPosition(formData)
    );
    if (success && result) {
      positions.value.push(result);
    }
    return success;
  }

  async function updatePosition(
    positionId: number | string,
    formData: Partial<PositionFormData>
  ): Promise<boolean> {
    const { success, result } = await runWithFlag(isUpdating, 'Failed to update position', () =>
      positionApi.updatePosition(positionId, formData)
    );
    if (success && result) {
      const index = positions.value.findIndex((p) => p.id === positionId);
      if (index !== -1) {
        positions.value[index] = result;
      }
    }
    return success;
  }

  async function deletePosition(positionId: number): Promise<boolean> {
    const { success } = await runWithFlag(isDeleting, 'Failed to delete position', () =>
      positionApi.deletePosition(positionId)
    );
    if (success) {
      positions.value = positions.value.filter((p) => p.id !== positionId);
    }
    return success;
  }

  async function bulkDeletePositions(
    positionIds: number[]
  ): Promise<{ success: boolean; deletedCount: number }> {
    const { success, result } = await runWithFlag(isDeleting, 'Failed to delete positions', () =>
      positionApi.bulkDeletePositions(positionIds)
    );

    if (!success || !result) {
      return { success: false, deletedCount: 0 };
    }

    const apiResult = result.data.data;
    positions.value = positions.value.filter((p) => !apiResult.succeededIds.includes(p.id));

    return {
      success: apiResult.successCount > 0,
      deletedCount: apiResult.successCount,
    };
  }

  function setPositions(newPositions: Position[]) {
    positions.value = newPositions;
  }

  function clearError() {
    error.value = null;
  }

  function clearCurrentPosition() {
    currentPosition.value = null;
  }

  function reset() {
    positions.value = [];
    currentPosition.value = null;
    isLoading.value = false;
    isCreating.value = false;
    isUpdating.value = false;
    isDeleting.value = false;
    error.value = null;
  }

  return {
    // State
    positions: readonly(positions),
    currentPosition: readonly(currentPosition),
    isLoading: readonly(isLoading),
    isCreating: readonly(isCreating),
    isUpdating: readonly(isUpdating),
    isDeleting: readonly(isDeleting),
    error: readonly(error),
    // Getters
    positionCount,
    hasPositions,
    activePositions,
    // Actions
    fetchAllPositions,
    fetchPositionById,
    createPosition,
    updatePosition,
    deletePosition,
    bulkDeletePositions,
    setPositions,
    clearError,
    clearCurrentPosition,
    reset,
  };
});
